import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ArrowRight,
  ArrowLeft,
  KeyRound,
  Shield,
  CheckCircle2,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import ApiClient from "../api/apiClient";
import loader from "../loader";
import methodModel from "../methods";
import FormControl from "../../components/common/FormControl";

export default function Reset() {
  const history = useNavigate();
  const WebDetails = useSelector((state) => state.logo);

  // Form State
  const [userId, setUserId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Validation & Loading State
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
    code: "",
  });
  const [loading, setLoading] = useState(false);

  // Read URL query parameters on mount
  useEffect(() => {
    const urlUserId =
      methodModel.getPrams("userId") || methodModel.getPrams("id");
    const urlCode =
      methodModel.getPrams("code") || methodModel.getPrams("verificationCode");
    const urlEmail = methodModel.getPrams("email");

    if (urlUserId) setUserId(urlUserId);
    if (urlCode) setVerificationCode(urlCode);
    if (urlEmail) setEmail(urlEmail);
  }, []);

  // Real-time Password Validation Handler matching Login & Super Admin portal
  const handlePasswordChange = (val) => {
    setPassword(val);
    if (!val) {
      setErrors((prev) => ({
        ...prev,
        password: "Please enter your new password.",
      }));
    } else {
      const check = methodModel.validateLoginPassword(val);
      setErrors((prev) => ({
        ...prev,
        password: check.success ? "" : check.message,
      }));
    }

    if (confirmPassword) {
      if (confirmPassword !== val) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      }
    }
  };

  // Real-time Confirm Password Validation Handler
  const handleConfirmPasswordChange = (val) => {
    setConfirmPassword(val);
    if (!val) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Please confirm your new password.",
      }));
    } else if (val !== password) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  // Submit Handler: calls user/admin/reset-password
  const handleSubmit = (e) => {
    e.preventDefault();

    let newErrors = {
      password: "",
      confirmPassword: "",
      code: "",
    };
    let hasError = false;

    // Retrieve verification code from state or URL
    const codeCleaned = (
      verificationCode ||
      methodModel.getPrams("code") ||
      methodModel.getPrams("verificationCode") ||
      ""
    ).trim();

    if (!codeCleaned) {
      newErrors.code = "Please enter the verification code.";
      toast.error(
        "Missing verification code. Please enter the OTP code sent to your email."
      );
      hasError = true;
    }

    // Validate password simultaneously using Login page validator
    const passwordValidation = methodModel.validateLoginPassword(password);
    if (!passwordValidation.success) {
      newErrors.password = passwordValidation.message;
      hasError = true;
    }

    // Validate confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password.";
      hasError = true;
    } else if (confirmPassword !== password) {
      newErrors.confirmPassword = "Passwords do not match.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({ password: "", confirmPassword: "", code: "" });
    setLoading(true);
    loader(true);

    const targetEmail = (email || methodModel.getPrams("email") || "").trim();
    const targetUserId =
      userId || methodModel.getPrams("userId") || methodModel.getPrams("id");

    const payload = {
      verificationCode: codeCleaned,
      otp: codeCleaned,
      password: password,
      newPassword: password,
    };

    if (targetUserId) {
      payload.userId = targetUserId;
      payload.id = targetUserId;
    }

    if (targetEmail) {
      payload.email = targetEmail;
    }

    // Primary endpoint: user/admin/reset-password
    ApiClient.post("user/admin/reset-password", payload, "", true)
      .then((res) => {
        loader(false);
        setLoading(false);
        if (res?.success) {
          toast.success(
            res?.message || "Password reset successfully! Please sign in."
          );
          setTimeout(() => {
            history(
              "/login?message=" +
                encodeURIComponent(
                  "Password reset successfully. Please sign in with your new password."
                )
            );
          }, 1000);
        } else {
          // Fallback to customer put endpoint user/reset/password if needed
          ApiClient.put("user/reset/password", payload, "", true)
            .then((custRes) => {
              if (custRes?.success) {
                toast.success(
                  custRes?.message ||
                    "Password reset successfully! Please sign in."
                );
                setTimeout(() => {
                  history(
                    "/login?message=" +
                      encodeURIComponent(
                        "Password reset successfully. Please sign in with your new password."
                      )
                  );
                }, 1000);
              } else {
                const errMsg =
                  custRes?.error?.message ||
                  custRes?.message ||
                  res?.error?.message ||
                  res?.message ||
                  "Failed to reset password. The link or verification code may have expired.";
                toast.error(errMsg);
              }
            })
            .catch(() => {
              const errMsg =
                res?.error?.message ||
                res?.message ||
                "Failed to reset password. The link or verification code may have expired.";
              toast.error(errMsg);
            });
        }
      })
      .catch((err) => {
        // Fallback attempt to user/reset/password
        ApiClient.put("user/reset/password", payload, "", true)
          .then((custRes) => {
            loader(false);
            setLoading(false);
            if (custRes?.success) {
              toast.success(
                custRes?.message ||
                  "Password reset successfully! Please sign in."
              );
              setTimeout(() => {
                history(
                  "/login?message=" +
                    encodeURIComponent(
                      "Password reset successfully. Please sign in with your new password."
                    )
                );
              }, 1000);
            } else {
              const errMsg =
                err?.response?.data?.error?.message ||
                err?.message ||
                "Failed to reset password. Please try again.";
              toast.error(errMsg);
            }
          })
          .catch(() => {
            loader(false);
            setLoading(false);
            const errMsg =
              err?.response?.data?.error?.message ||
              err?.message ||
              "Failed to reset password. Please try again.";
            toast.error(errMsg);
          });
      });
  };

  const logoSrc = WebDetails?.logo || "/assets/img/logo.png";
  const hasUrlCode = Boolean(
    methodModel.getPrams("code") || methodModel.getPrams("verificationCode")
  );

  return (
    <>
      <div className="h-screen w-full flex flex-col lg:flex-row bg-white lg:bg-[#081325] overflow-hidden antialiased selection:bg-[#115B9D] selection:text-white">
        {/* ======================================================== */}
        {/* LEFT BRAND & PLATFORM SHOWCASE PANEL (Visible on lg+) */}
        {/* ======================================================== */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[52%] h-full relative flex-col justify-between p-6 xl:p-10 2xl:p-12 overflow-hidden bg-gradient-to-br from-[#061224] via-[#0B2545] to-[#115B9D] text-white select-none">
          {/* Ambient Glow Orbs */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/3 w-80 h-80 bg-[#115B9D]/30 rounded-full blur-3xl pointer-events-none" />

          {/* Subtle Grid Pattern Overlay */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />

          {/* Top Branding Bar */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/15 shadow-xl">
              <img
                src={logoSrc}
                alt="Charles S. Smith & Associates"
                className="h-9 w-auto object-contain bg-white rounded-lg p-1 shadow-sm"
              />
              <div className="leading-tight">
                <span className="block text-[10px] uppercase font-bold tracking-widest text-sky-300">
                  E-Commerce Suite
                </span>
                <span className="block text-xs font-semibold text-white tracking-tight">
                  Charles S. Smith & Associates
                </span>
              </div>
            </div>

            {/* Live Status Pill */}
            <div className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Systems Operational
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-4 xl:py-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 border border-sky-300/30 text-sky-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <Shield className="w-3.5 h-3.5 text-sky-300" />
              Secure Authentication
            </div>

            <h1 className="text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Create New Administrator{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-sky-200 to-white">
                Access Credentials
              </span>
            </h1>

            <p className="mt-2.5 text-xs xl:text-sm text-sky-100 leading-relaxed max-w-lg font-normal">
              Configure a high-entropy password to regain secure administrator access to the platform.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-sky-400/20 flex items-center justify-center text-sky-300 mb-2">
                  <KeyRound className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  OTP Verified
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Cryptographically verified via secure link.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-indigo-400/20 flex items-center justify-center text-indigo-300 mb-2">
                  <Shield className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Strong Entropy
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Enforces enterprise security standards.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-emerald-400/20 flex items-center justify-center text-emerald-300 mb-2">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Direct Sign-In
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Instant redirection to super admin gateway.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Trust & Heritage Footer */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-sky-100">
            <span className="font-semibold text-white">
              Serving the Americas Since 1976
            </span>
            <span className="flex items-center gap-1.5 text-sky-200 font-medium">
              <Lock className="w-3.5 h-3.5 text-sky-300" /> 256-Bit SSL Encrypted
            </span>
          </div>
        </div>

        {/* ======================================================== */}
        {/* RIGHT FORM PANEL (Matches Screenshot 3) */}
        {/* ======================================================== */}
        <div className="w-full lg:w-1/2 xl:w-[48%] h-full flex items-center justify-center px-6 py-6 sm:px-10 sm:py-8 lg:px-12 xl:px-16 bg-white overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[420px] my-auto py-2">
            {/* Header with Logo */}
            <div className="text-center mb-5 sm:mb-6">
              <img
                src={logoSrc}
                alt="Platform Logo"
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                Reset Password
              </h2>
              <p className="text-xs text-slate-700 mt-1 leading-relaxed font-medium">
                Enter your new password to secure your account.
              </p>

            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-3.5 sm:space-y-4"
            >
              {/* Verification Code input if not already in URL */}
              {!hasUrlCode && (
                <FormControl
                  type="text"
                  name="verificationCode"
                  id="resetCode"
                  label="Verification Code (OTP)"
                  required={true}
                  placeholder="Enter 6-digit verification code"
                  value={verificationCode}
                  error={errors.code}
                  onChange={(val) => {
                    setVerificationCode(val);
                    setErrors((prev) => ({ ...prev, code: "" }));
                  }}
                  autoComplete="one-time-code"
                  divClass="space-y-1"
                  labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                  className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
                />
              )}

              {/* New Password using platform FormControl with showEye & Login validator */}
              <FormControl
                type="password"
                name="newPassword"
                id="resetNewPassword"
                label="New Password"
                required={true}
                showEye={true}
                placeholder="Enter new password (min. 8 characters)"
                value={password}
                error={errors.password}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
              />

              {/* Confirm Password using platform FormControl with showEye */}
              <FormControl
                type="password"
                name="confirmPassword"
                id="resetConfirmPassword"
                label="Confirm New Password"
                required={true}
                showEye={true}
                placeholder="Re-enter your new password"
                value={confirmPassword}
                error={errors.confirmPassword}
                onChange={handleConfirmPasswordChange}
                autoComplete="new-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
              />

              {/* Password Requirement Hint */}
              <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600 space-y-1">
                <span className="font-bold text-slate-800">
                  Password Requirements:
                </span>
                <p>
                  8-32 characters, uppercase letter, lowercase letter, number,
                  and special symbol.
                </p>
              </div>

              {/* Submit Action Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <span>
                    {loading
                      ? "Resetting Password..."
                      : "Reset Password & Sign In"}
                  </span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </form>

            {/* Back to Sign In Link */}
            <div className="mt-5 pt-3.5 border-t border-slate-200 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>
                  Remember your password?{" "}
                  <strong className="text-[#115B9D] font-bold">Sign In</strong>
                </span>
              </Link>
            </div>

            {/* Security & Support Footer */}
            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-[#115B9D]" /> 256-Bit SSL Encrypted
              </span>
              <span className="font-semibold text-slate-800">
                Authorized Personnel Only
              </span>
            </div>

            {/* Copyright Note */}
            <p className="text-center text-[11px] text-slate-600 mt-2 font-medium">
              &copy; {new Date().getFullYear()} Charles S. Smith & Associates. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" />
    </>
  );
}
